// components/cards/MedicationCard.tsx
import React from 'react';
import { Edit3, Copy, Trash2 } from 'lucide-react';
import type { Medication } from '../../types/medication';

interface MedicationCardProps {
  medication: Medication;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  showActions?: boolean;
}

const MedicationCard: React.FC<MedicationCardProps> = ({
  medication,
  onEdit,
  onDelete,
  onDuplicate,
  showActions = true
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "border-red-500 bg-red-50";
      case "important":
        return "border-orange-500 bg-orange-50";
      default:
        return "border-blue-500 bg-blue-50";
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800";
      case "important":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  return (
    <div
      className={`rounded-xl p-4 border-l-4 transition-all duration-200 hover:shadow-md ${getPriorityColor(
        medication.priority
      )}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center mb-3 gap-2">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadgeColor(
                medication.priority
              )}`}
            >
              {medication.priority.toUpperCase()}
            </span>
            <h4 className="font-semibold text-lg text-gray-800 uppercase">
              {medication.name}
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
            <p>
              <span className="font-medium">N° d'ordre:</span>{" "}
              {medication.orderNumber}
            </p>
            <p>
              <span className="font-medium">Quantité:</span>{" "}
              {medication.quantity} {medication.quantityUnit}
            </p>
            <p>
              <span className="font-medium">Dose:</span>{" "}
              {medication.dose} {medication.doseUnit}
            </p>
            <p>
              <span className="font-medium">À prendre:</span>{" "}
              {medication.frequency} {medication.interval}
            </p>
            <p>
              <span className="font-medium">Durée:</span>{" "}
              {medication.duration} {medication.durationUnit}
            </p>
            <p>
              <span className="font-medium">Du:</span>{" "}
              {medication.startDate}
            </p>
            <p>
              <span className="font-medium">Au:</span>{" "}
              {medication.endDate}
            </p>
          </div>
          {medication.advice && (
            <p className="mt-2 text-sm text-gray-700 bg-yellow-50 p-2 rounded border-l-2 border-yellow-400">
              <span className="font-medium">Conseil:</span>{" "}
              {medication.advice}
            </p>
          )}
        </div>
        {showActions && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 ml-0 sm:ml-2">
            <button
              onClick={onEdit}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Modifier"
            >
              <Edit3 size={18} />
            </button>
            <button
              onClick={onDuplicate}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Dupliquer"
            >
              <Copy size={18} />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Supprimer"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicationCard;